/**
 * ATIVIDADE RECENTE DA ENGENHARIA e RESUMO DE CORREÇÕES.
 *
 * Funções puras: recebem o que já está gravado e devolvem os dois blocos do
 * Painel. Nenhum número é inventado e nenhuma lista é de exemplo.
 *
 * ── DE ONDE VEM A ATIVIDADE ───────────────────────────────────────────────
 *
 * **Não** da trilha de auditoria: no app real o `AuditRecorder` injetado é um
 * `mockAudit` que descarta todo evento (CLAUDE.md §17). Um feed montado sobre
 * ela viria permanentemente vazio.
 *
 * A fonte é o que de fato persiste: o `history` de cada revisão técnica
 * (`TechnicalReviewHistoryEntry` — quem, quando, qual ação, com rótulo
 * oficial em `TECHNICAL_REVIEW_HISTORY_LABELS`) somado aos pedidos de
 * correção por item, que gravam autor e instante na abertura e na resolução.
 * São os mesmos registros que sustentam o histórico do documento, de modo
 * que o Painel nunca conta uma história diferente da do próprio documento.
 */

import {
  TECHNICAL_REVIEW_HISTORY_LABELS,
  type TechnicalReviewHistoryEntry
} from "./technical-document-review.js";
import type { TechnicalCorrectionRequest, TechnicalCorrectionStatus } from "./technical-correction.js";

/** Um evento pronto para a lista "ATIVIDADE RECENTE". */
export interface EngineeringActivityEntry {
  /** Instante ISO — base da ordenação e do tempo relativo. */
  at: string;
  /** Quem fez. Nome resolvido; nunca um id cru na tela. */
  actorName: string;
  /** Rótulo oficial da ação ("Correção solicitada"). */
  action: string;
  /** Como o item é chamado na tela: a TAG quando existe, senão o número. */
  itemLabel: string;
  /** Complemento curto, quando o registro traz um. */
  detail: string;
  /** Para onde a linha leva, quando há rota conhecida. */
  route: string | null;
}

/** Uma revisão, no mínimo que este módulo precisa ler. */
export interface ActivitySourceReview {
  documentNumber: string;
  tag?: string | null;
  route?: string | null;
  history?: readonly TechnicalReviewHistoryEntry[] | undefined;
}

const text = (value: unknown): string => (value === null || value === undefined ? "" : String(value));

/**
 * Nome de quem agiu. `by` é o campo que o registro grava; quando ele vem
 * vazio, dizer "—" é melhor que imprimir um id — quem lê o Painel não
 * reconhece um UUID, e um id na tela parece defeito.
 */
function actorOf(by: unknown, fallback?: string | null): string {
  const name = text(by).trim();
  if (name) return name;
  const alternative = text(fallback).trim();
  return alternative || "—";
}

/**
 * Junta as duas fontes numa linha do tempo única, da mais recente para a
 * mais antiga. Evento sem instante é descartado: sem `at` não há onde
 * colocá-lo na ordem, e inventar "agora" o poria no topo como se tivesse
 * acabado de acontecer.
 */
export function mergeEngineeringActivity(
  reviews: readonly ActivitySourceReview[],
  corrections: readonly TechnicalCorrectionRequest[],
  options: { limit?: number } = {}
): EngineeringActivityEntry[] {
  const entries: EngineeringActivityEntry[] = [];

  for (const review of reviews) {
    const label = text(review.tag).trim() || text(review.documentNumber).trim() || "—";
    for (const event of review.history ?? []) {
      const at = text(event.at).trim();
      if (!at) continue;
      entries.push({
        at,
        actorName: actorOf(event.by),
        action: TECHNICAL_REVIEW_HISTORY_LABELS[event.action] ?? text(event.action),
        itemLabel: label,
        detail: text(event.detail).trim(),
        route: text(review.route).trim() || null
      });
    }
  }

  for (const correction of corrections) {
    const label = text(correction.documentNumber).trim() || "—";
    const opened = text(correction.createdAt).trim();
    if (opened) {
      entries.push({
        at: opened,
        actorName: actorOf(correction.requestedBy?.name),
        action: "Correção solicitada",
        itemLabel: label,
        // A seção/item é o que diz ONDE a correção incide — é o detalhe útil.
        detail: [correction.sectionLabel, correction.itemLabel].map(v => text(v).trim()).filter(Boolean).join(" · "),
        route: text(correction.anchor).trim() || null
      });
    }
    const resolved = text(correction.resolvedAt).trim();
    if (resolved) {
      entries.push({
        at: resolved,
        actorName: actorOf(correction.resolvedBy?.name, correction.assignedTo?.name),
        action: correction.status === "CANCELADA" ? "Correção cancelada" : "Correção executada",
        itemLabel: label,
        detail: text(correction.resolutionComment).trim(),
        route: text(correction.anchor).trim() || null
      });
    }
  }

  entries.sort((a, b) => b.at.localeCompare(a.at));
  const limit = options.limit ?? 8;
  return limit > 0 ? entries.slice(0, limit) : entries;
}

/**
 * Tempo relativo em português, a partir de um instante ISO.
 *
 * Sem instante devolve "—", nunca "Há 0 minutos": zero minutos afirma que
 * algo acabou de acontecer, e o que existe é ausência de data.
 */
export function relativeSince(at: string | null | undefined, now: Date = new Date()): string {
  const iso = text(at).trim();
  if (!iso) return "—";
  const stamp = Date.parse(iso);
  if (!Number.isFinite(stamp)) return "—";
  const diff = now.getTime() - stamp;
  if (diff < 0) return "Agora";
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "Agora";
  if (minutes < 60) return `Há ${minutes} minuto${minutes === 1 ? "" : "s"}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Há ${hours} hora${hours === 1 ? "" : "s"}`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `Há ${days} dia${days === 1 ? "" : "s"}`;
  const months = Math.floor(days / 30);
  if (months < 12) return `Há ${months} ${months === 1 ? "mês" : "meses"}`;
  const years = Math.floor(months / 12);
  return `Há ${years} ano${years === 1 ? "" : "s"}`;
}

/**
 * RESUMO DE CORREÇÕES — pelos estados que EXISTEM no domínio.
 *
 * O item de correção tem três estados reais: `PENDENTE`, `CORRIGIDA` e
 * `CANCELADA` (`TechnicalCorrectionStatus`). Não existe "correção em
 * análise" nem "correção aprovada/rejeitada" — quem é analisado e aprovado
 * é o DOCUMENTO, depois de as correções serem executadas.
 *
 * Por isso o bloco mistura, de propósito, duas grandezas que a tela precisa
 * lado a lado e rotula cada uma pelo que ela é: três contagens de ITENS de
 * correção mais uma de DOCUMENTOS que voltaram ao PLH. Inventar um quarto
 * estado de item para a tela ficar simétrica faria o Painel afirmar algo
 * que o domínio não sustenta.
 */
export interface CorrectionSummary {
  /** Itens em aberto, aguardando execução pelo autor do documento. */
  pending: number;
  /** Itens já executados. */
  resolved: number;
  /** Itens cancelados por quem os pediu. */
  cancelled: number;
  /** DOCUMENTOS corrigidos que voltaram para reanálise do PLH. */
  awaitingReanalysis: number;
  total: number;
}

export function summarizeCorrections(
  corrections: readonly Pick<TechnicalCorrectionRequest, "status">[],
  awaitingReanalysis = 0
): CorrectionSummary {
  const count = (status: TechnicalCorrectionStatus) => corrections.filter(c => c.status === status).length;
  return {
    pending: count("PENDENTE"),
    resolved: count("CORRIGIDA"),
    cancelled: count("CANCELADA"),
    awaitingReanalysis,
    total: corrections.length
  };
}
